import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { UploadProduct } from "types/products";

interface ProductPreviewProps {
  products: UploadProduct[];
  onUpdate?: (index: number, updatedProduct: UploadProduct) => void;
  onDelete?: (index: number) => void;
}

interface ValidationErrors {
  name?: string;
  amazonId?: string;
  sku?: string;
  brand?: string;
  upc?: string;
  map?: string;
  trademark?: string;
}

const headStyle = {
  fontWeight: 600,
  fontSize: 14,
  color: "text.primary",
  py: 2,
  px: 3,
};

const cellStyle = {
  fontSize: 14,
  py: 2.5,
  px: 3,
};

const validateProduct = (product: UploadProduct): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!product.name || product.name.trim().length === 0) {
    errors.name = "Name is required";
  }

  if (!product.amazonId || product.amazonId.trim().length === 0) {
    errors.amazonId = "ASIN is required";
  } else if (!/^B[0-9A-Z]{9}$/i.test(product.amazonId)) {
    errors.amazonId = "Invalid ASIN format (B + 9 characters)";
  }

  if (product.upc && !/^\d{12}$/.test(product.upc)) {
    errors.upc = "UPC must be 12 digits";
  }

  if (product.map) {
    const cleanedMap = product.map.replace(/[$,]/g, "");
    const mapValue = parseFloat(cleanedMap);
    if (isNaN(mapValue) || mapValue < 0) {
      errors.map = "MAP must be a valid positive number";
    }
  }

  return errors;
};

const ProductPreview: React.FC<ProductPreviewProps> = ({
  products,
  onUpdate,
  onDelete,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedProduct, setEditedProduct] = useState<UploadProduct | null>(
    null
  );
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditedProduct({ ...products[index] });
    setValidationErrors({});
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedProduct(null);
    setValidationErrors({});
  };

  const handleSave = (index: number) => {
    if (!editedProduct) return;

    const errors = validateProduct(editedProduct);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onUpdate?.(index, editedProduct);
    setEditingIndex(null);
    setEditedProduct(null);
    setValidationErrors({});
  };

  const handleDelete = (index: number) => {
    onDelete?.(index);
  };

  const handleFieldChange = (field: keyof UploadProduct, value: string) => {
    if (!editedProduct) return;
    setEditedProduct({ ...editedProduct, [field]: value });
  };

  const renderCell = (
    field: keyof UploadProduct,
    value: string,
    isEditing: boolean
  ) => {
    if (isEditing && editedProduct) {
      const hasError = !!validationErrors[field];

      return (
        <Tooltip
          title={validationErrors[field] || ""}
          open={hasError}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                border: "1px solid error.main",
                "& .MuiTooltip-arrow": {
                  color: "error.main",
                },
              },
            },
          }}
        >
          <TextField
            value={editedProduct[field] || ""}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            error={hasError}
            size="small"
            fullWidth
            variant="standard"
            sx={{
              mt: 1,
              "& .MuiInput-root": {
                "&:before": {
                  borderColor: hasError ? "error.main" : undefined,
                },
              },
            }}
          />
        </Tooltip>
      );
    }
    return value;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: 500,
          overflowY: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={headStyle}>Name</TableCell>
              <TableCell sx={headStyle}>ASIN</TableCell>
              <TableCell sx={headStyle}>SKU</TableCell>
              <TableCell sx={headStyle}>Brand</TableCell>
              <TableCell sx={headStyle}>UPC</TableCell>
              <TableCell sx={headStyle}>MAP</TableCell>
              <TableCell sx={{ ...headStyle, width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product, index) => {
              const isEditing = editingIndex === index;
              return (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      bgcolor: "grey.50",
                    },
                    "&:last-child td": {
                      borderBottom: 0,
                    },
                    bgcolor: isEditing ? "action.hover" : "transparent",
                  }}
                >
                  <TableCell sx={cellStyle}>
                    {renderCell("name", product.name, isEditing)}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {renderCell("amazonId", product.amazonId, isEditing)}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {renderCell("sku", product.sku || "", isEditing)}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {renderCell("brand", product.brand || "", isEditing)}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {renderCell("upc", product.upc || "", isEditing)}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {renderCell("map", product.map || "", isEditing)}
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, whiteSpace: "nowrap" }}>
                    {isEditing ? (
                      <>
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={() => handleSave(index)}
                            color="primary"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handleCancel}
                            color="default"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(index)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductPreview;
